import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeviceCreate } from '../../../api/models';
import { deviceService, DeviceFirmwareVersion, TemplateForDevice } from '../../../services/deviceService';
import { typesService, ModelReference } from '../../../services/typesService';
import { clientService } from '../../../services/clientService';
import { Client } from '../../../types/client';
import { DeviceDetailCore } from '../useDeviceDetail';

export const CONF47_TYPES = ['voice', 'local_alarm', 'dtmf_server'] as const;

/**
 * The Basic panel: the device row (client, gateway type, model, phone,
 * address, postal code, conf47 type, voice destinations) with its edit
 * form, its dates, the firmware it reported, and the name of the template
 * it follows (apply, reset). The configuration values live in Settings.
 */
export const useDevicesDetailsBasic = (core: DeviceDetailCore) => {
  const { device, isOpen, deviceDetail, setDeviceDetail, reloadDetail, setError, hardwareTypes, onDeviceUpdated } = core;

  // Identity
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modelRefs, setModelRefs] = useState<ModelReference[]>([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [editedPhone, setEditedPhone] = useState('');
  const [editedAddress, setEditedAddress] = useState('');
  const [editedPostalCode, setEditedPostalCode] = useState('');
  const [editedConf47Type, setEditedConf47Type] = useState('');
  const [editedNeedsRefresh, setEditedNeedsRefresh] = useState(true);
  
  const [editedVoiceDsts, setEditedVoiceDsts] = useState('');

  // Firmware
  const [firmwareVersions, setFirmwareVersions] = useState<DeviceFirmwareVersion[]>([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);

  // Template
  const [templates, setTemplates] = useState<TemplateForDevice[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    clientService.fetchAllClients().then(setClients).catch(console.error);
  }, []);

  // The edit form starts from the detail (the full row) and, until it is
  // loaded, from the list row.
  const resetEditForm = useCallback(() => {
    if (!device) return;
    setSelectedTypeCode(deviceDetail?.deviceType || device.deviceType || '');
    setSelectedModelId(deviceDetail?.hardware_model_id != null ? String(deviceDetail.hardware_model_id) : '');
    setSelectedClientId(deviceDetail?.idClient || device.idClient || '');
    const phone = deviceDetail?.phone || device.phone || '';
    setEditedPhone(phone === '000000000000000' ? '' : phone);
    setEditedAddress(deviceDetail?.address || '');
    setEditedPostalCode(deviceDetail?.postal_code || '');
    setEditedConf47Type(deviceDetail?.conf47Type || device.conf47Type || '');
    setEditedVoiceDsts((deviceDetail?.mqtt_voice_dsts || []).join(', '));
    setEditedNeedsRefresh(Boolean(deviceDetail?.needsRefresh ?? device.needsRefresh));
  }, [device, deviceDetail]);

  // Open: load firmware and templates, seed the edit form.
  useEffect(() => {
    if (!isOpen || !device?.idDevice) return;
    setIsEditing(false);
    resetEditForm();

    setFirmwareLoading(true);
    deviceService.getDeviceFirmwareVersions(device.idDevice)
      .then((versions) => setFirmwareVersions(Array.isArray(versions) ? versions : []))
      .catch((err) => {
        console.error('Failed to load firmware versions:', err);
        setFirmwareVersions([]);
      })
      .finally(() => setFirmwareLoading(false));

    setTemplatesLoading(true);
    deviceService.getTemplatesForDevice(device.idDevice)
      .then(setTemplates)
      .catch((err) => console.error('Failed to load templates:', err))
      .finally(() => setTemplatesLoading(false));

    if (device.idClient) {
      clientService.getClientName(device.idClient)
        .then(setClientName)
        .catch((err) => {
          console.error('Failed to load client name:', err);
          setClientName(device.idClient);
        });
    } else {
      setClientName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, device?.idDevice]);

  // Once the detail arrives, the form (when not being edited) follows it.
  useEffect(() => {
    if (!isEditing) resetEditForm();
  }, [deviceDetail, isEditing, resetEditForm]);

  // The template select follows the detail once it is loaded.
  useEffect(() => {
    setSelectedTemplateId(deviceDetail?.template_id ?? null);
  }, [deviceDetail?.template_id]);

  // Model references follow the selected gateway type.
  useEffect(() => {
    const hwType = selectedTypeCode ? hardwareTypes.find((t) => t.code === selectedTypeCode) : undefined;
    if (!hwType) {
      setModelRefs([]);
      return;
    }
    typesService.getModelReferences(hwType.id, false).then(setModelRefs).catch(console.error);
  }, [selectedTypeCode, hardwareTypes]);

  const currentModel = useMemo(
    () => modelRefs.find((r) => r.id === deviceDetail?.hardware_model_id),
    [modelRefs, deviceDetail?.hardware_model_id],
  );

  const cancelEdit = () => {
    setIsEditing(false);
    resetEditForm();
  };

  const parseVoiceDsts = (raw: string): string[] | null => {
    const items = raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    return items.length > 0 ? items : null;
  };

  const handleSave = async () => {
    if (!device) return;
    setSaving(true);
    setError(null);
    try {
      await deviceService.patchDevice(device.idDevice, {
        deviceType: selectedTypeCode || undefined,
        idClient: selectedClientId || undefined,
        phone: editedPhone || undefined,
        address: editedAddress.trim() || null,
        postal_code: editedPostalCode.trim() || null,
        hardware_model_id: selectedModelId ? Number(selectedModelId) : null,
        conf47Type: editedConf47Type || undefined,
        mqtt_voice_dsts: parseVoiceDsts(editedVoiceDsts),
        needsRefresh: editedNeedsRefresh,
      });

      const selectedClient = clients.find((c) => c.id === selectedClientId);
      if (selectedClient) setClientName(selectedClient.name);

      setIsEditing(false);
      await reloadDetail();
      onDeviceUpdated?.({
        ...device,
        deviceType: selectedTypeCode,
        idClient: selectedClientId,
        phone: editedPhone,
        conf47Type: editedConf47Type,
        needsRefresh: editedNeedsRefresh,
      } as DeviceCreate);
    } catch (err: any) {
      setError(err.message || 'Failed to update device');
    } finally {
      setSaving(false);
    }
  };

  const notifyConfigChange = (detail: NonNullable<typeof deviceDetail>) => {
    if (!device) return;
    const cfg = (detail.effective_config || {}) as Record<string, any>;
    onDeviceUpdated?.({
      ...device,
      mqttHost: cfg.mqtt_host,
      mqttPort: cfg.mqtt_port,
      syslogHost: cfg.syslog_host,
      syslogPort: cfg.syslog_port,
    } as DeviceCreate);
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !device) return;
    setApplying(true);
    setError(null);
    try {
      const result = await deviceService.applyTemplate(device.idDevice, selectedTemplateId);
      setDeviceDetail(result);
      notifyConfigChange(result);
    } catch (err) {
      setError('Failed to apply template');
      console.error('Failed to apply template:', err);
    } finally {
      setApplying(false);
    }
  };

  const handleResetToTemplate = async () => {
    if (!deviceDetail?.template_id || !device) return;
    setResetting(true);
    setError(null);
    try {
      const result = await deviceService.resetToTemplate(device.idDevice);
      setDeviceDetail(result);
      notifyConfigChange(result);
    } catch (err) {
      setError('Failed to reset to template');
      console.error('Failed to reset to template:', err);
    } finally {
      setResetting(false);
    }
  };

  const firmwareSummary = useMemo(
    () => firmwareVersions.map((fw) => `${fw.component_type.toLowerCase()} ${fw.version}`).join('; '),
    [firmwareVersions],
  );

  const currentTemplate = templates.find((t) => t.id === deviceDetail?.template_id);
  const hasChangedTemplate = selectedTemplateId !== (deviceDetail?.template_id ?? null);

  return {
    isEditing, setIsEditing, saving, cancelEdit, handleSave,
    modelRefs, currentModel, selectedTypeCode, setSelectedTypeCode, selectedModelId, setSelectedModelId,
    clientName, clients, selectedClientId, setSelectedClientId,
    editedPhone, setEditedPhone, editedAddress, setEditedAddress, editedPostalCode, setEditedPostalCode,
    editedConf47Type, setEditedConf47Type, editedVoiceDsts, setEditedVoiceDsts,
    firmwareVersions, firmwareLoading, firmwareSummary,
    templates, selectedTemplateId, setSelectedTemplateId, templatesLoading, applying, resetting,
    handleApplyTemplate, handleResetToTemplate, editedNeedsRefresh, setEditedNeedsRefresh,
    currentTemplate, hasChangedTemplate,
  };
};
